import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip as ChartTooltip } from "chart.js"
import { faArrowUpRight, faArrowDownRight } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Typography, Box, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Line } from "react-chartjs-2"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip)

const useStyles = makeStyles()((theme) => ({
    wrap: {
        width: "9rem",
        height: "3.5rem",
        margin: "0 auto",
    },
    infoWrap: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.5rem",
    },
    title: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
    },
    caption: {
        color: theme.palette.tailwind.slate[400],
    },
}))

type StatisticGraphProps = Readonly<{
    changeInPercentage: number
    labels: string[]
    datasets: number[]
    title: string | number
}>

export function StatisticGraph({ changeInPercentage, labels, datasets, title }: StatisticGraphProps) {
    const { classes } = useStyles()
    const isPositiveTrend = changeInPercentage >= 0
    const percentageTrendColor = isPositiveTrend ? "#22C55E" : "#EF4444"

    const chartData = {
        labels,
        datasets: [
            {
                data: datasets,
                borderColor: percentageTrendColor,
                borderWidth: 2,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 4,
                pointHoverBackgroundColor: percentageTrendColor,
            },
        ],
    }

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: "index" as const,
        },
        scales: {
            x: {
                display: false,
            },
            y: {
                display: false,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    }

    return (
        <Stack gap={2}>
            <Box className={classes.wrap}>
                <Line data={chartData} options={chartOptions} />
            </Box>

            <Stack alignItems="center" gap={2}>
                <Typography variant="h4" className={classes.title}>
                    {title}
                </Typography>
                <Stack>
                    <Typography variant="subtitle2" color={percentageTrendColor} className={classes.infoWrap}>
                        <FontAwesomeIcon icon={isPositiveTrend ? faArrowUpRight : faArrowDownRight} size="lg" />
                        <Typography variant="body1">{changeInPercentage.toFixed(2)}%</Typography>
                    </Typography>
                    <LocalizedStrict id="merchant-home-statistics-compared-to-last-period">
                        <Typography variant="caption" className={classes.caption}>
                            compared to last period
                        </Typography>
                    </LocalizedStrict>
                </Stack>
            </Stack>
        </Stack>
    )
}
